import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Inbox, BarChart3 } from 'lucide-react';
import { isDevEnvironment } from '../services/api';

// Lead Manager and GI both work the same lead data, so they're kept as a
// visually grouped pair (icons + a divider) set apart from the more distinct
// admin sections (Appointments, Monitoring, Admin), which stay plain text.
const LEAD_NAV_LINKS = [
  { to: '/', label: 'Lead Manager', adminOnly: false },
  { to: '/gi', label: 'GI', adminOnly: true, title: 'GI Dashboard' },
];

const ADMIN_NAV_LINKS = [
  { to: '/appointments', label: 'Appointments', adminOnly: true },
  { to: '/appointments-v2', label: 'Appointments V2', adminOnly: true, devOnly: true, prodSuperAdmin: true },
  { to: '/admin/monitoring', label: 'Monitoring', adminOnly: true },
  { to: '/admin', label: 'Admin', adminOnly: true },
];

const navLinkClasses = (isActive) =>
  `flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
    isActive
      ? 'border-purple-600 text-purple-700'
      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
  }`;

const AppHeader = ({ user, onLogout, badgeCount, badgeLabel }) => {
  const location = useLocation();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  return (
    <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-2 sm:py-3 flex-shrink-0 shadow-sm w-full">
      <div className="flex flex-wrap items-center justify-between gap-y-2 w-full">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
            {getPageTitle(location.pathname)}
          </h1>
          {badgeCount !== undefined && (
            <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
              {badgeCount} {badgeLabel || 'Items'}
            </span>
          )}
          <span className="text-sm text-gray-600 hidden md:inline ml-4">
            Welcome, <span className="font-medium">{user?.username}</span>
          </span>
        </div>

        <div className="flex items-center gap-1 max-w-full overflow-x-auto">
          <nav className="flex items-center flex-nowrap mr-1 sm:mr-2" aria-label="Primary">
            <div className={`flex items-center gap-1 flex-nowrap ${isAdmin ? 'pr-2 sm:pr-3 mr-2 sm:mr-3 border-r border-gray-200' : ''}`}>
              {LEAD_NAV_LINKS.filter((link) => !link.adminOnly || isAdmin).map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    title={link.title}
                    className={navLinkClasses(location.pathname === link.to)}
                  >
                    {Icon && <Icon size={14} />}
                    {link.label}
                  </Link>
                );
              })}
            </div>
            <div className="flex items-center gap-1 flex-nowrap">
              {ADMIN_NAV_LINKS.filter((link) => (!link.adminOnly || isAdmin) && (!link.devOnly || isDevEnvironment() || (link.prodSuperAdmin && isSuperAdmin))).map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  title={link.title}
                  className={navLinkClasses(location.pathname === link.to)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>
          <button
            onClick={onLogout}
            className="flex items-center px-2 sm:px-3 py-1.5 text-sm font-medium rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors flex-shrink-0"
          >
            <LogOut size={14} className="sm:mr-1.5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const getPageTitle = (pathname) => {
  switch (pathname) {
    case '/':
      return 'Lead Manager';
    case '/gi':
      return 'GI Dashboard';
    case '/appointments':
      return 'Appointments Management';
    case '/appointments-v2':
      return 'Appointments';
    case '/admin/monitoring':
      return 'Monitoring';
    case '/admin':
      return 'Admin Panel';
    default:
      return 'Dashboard';
  }
};

export default AppHeader;
